/**
 * SSE run-stream client for `POST /api/agents/{id}/run`.
 *
 * The browser `EventSource` API cannot attach an `Authorization` header (and
 * only supports GET), so this helper uses `fetch` with a streaming body reader
 * to consume the `text/event-stream` response while still injecting the JWT
 * bearer token the api client uses. It parses `event:`/`data:` frames and
 * invokes `onEvent` for each decoded {@link RunEvent}.
 *
 * Returns an abortable handle: call `.cancel()` to abort the in-flight stream.
 */

import { AUTH_TOKEN_KEY, config } from './config';
import type { RunAgentRequest, RunEvent } from './agents';

export interface RunStreamHandle {
  /** Resolves when the stream completes (or rejects on a transport error). */
  done: Promise<void>;
  /** Abort the in-flight stream. */
  cancel: () => void;
}

interface RunStreamOptions {
  onEvent: (event: RunEvent) => void;
  onError?: (error: Error) => void;
  signal?: AbortSignal;
}

function getAuthToken(): string | null {
  try {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  } catch {
    return null;
  }
}

/** Parse a single SSE frame (a block separated by a blank line) into a RunEvent. */
function parseFrame(frame: string): RunEvent | null {
  let dataLine = '';
  for (const rawLine of frame.split('\n')) {
    const line = rawLine.replace(/\r$/, '');
    if (line.startsWith('data:')) {
      // Concatenate multi-line data per the SSE spec.
      dataLine += line.slice(5).trimStart();
    }
    // `event:` / `id:` lines are informational; the decoded JSON carries
    // `event_type` which we rely on, so we don't need to read them separately.
  }
  if (!dataLine) return null;
  try {
    return JSON.parse(dataLine) as RunEvent;
  } catch {
    return null;
  }
}

/**
 * Start consuming the SSE run stream for an agent. Each parsed event is passed
 * to `onEvent`; transport/parse errors go to `onError`. The returned handle
 * lets the caller cancel the stream (e.g. on unmount or a Cancel button).
 */
export function runAgentStream(
  agentId: string,
  body: RunAgentRequest,
  options: RunStreamOptions,
): RunStreamHandle {
  const controller = new AbortController();
  if (options.signal) {
    if (options.signal.aborted) controller.abort();
    else options.signal.addEventListener('abort', () => controller.abort(), { once: true });
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  };
  const token = getAuthToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const done = (async (): Promise<void> => {
    const response = await fetch(`${config.apiBaseUrl}/agents/${agentId}/run`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      signal: controller.signal,
    });

    if (!response.ok || !response.body) {
      throw new Error(`Run failed: HTTP ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { value, done: streamDone } = await reader.read();
      if (streamDone) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE frames are delimited by a blank line (\n\n).
      let sepIndex: number;
      while ((sepIndex = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, sepIndex);
        buffer = buffer.slice(sepIndex + 2);
        const event = parseFrame(frame);
        if (event) options.onEvent(event);
      }
    }

    // Flush any trailing frame without a terminating blank line.
    const tail = parseFrame(buffer);
    if (tail) options.onEvent(tail);
  })();

  const guarded = done.catch((err: unknown) => {
    if (controller.signal.aborted) return; // intentional cancel — not an error
    const error = err instanceof Error ? err : new Error(String(err));
    if (options.onError) options.onError(error);
    else throw error;
  });

  return {
    done: guarded,
    cancel: () => controller.abort(),
  };
}
